import { CommonProps } from '@va/types/component';
import { TooltipOptions } from '@va/types/tooltip';
import { TooltipWrapper } from '@va/ui/tooltips';
import { useCheckOverflow, useIsFocused, useMergeRefs } from '@va/util/hooks';
import classNames from 'classnames';
import { isNil, isString } from 'lodash';
import { MouseEventHandler, ReactNode, forwardRef, useMemo, useRef } from 'react';
import './button.scss';

const BASE_CLASSES = `visa-base-button gap-[0.6875rem] group flex items-center text-15 leading-24 font-medium whitespace-nowrap overflow-hidden relative`;
const SIZE_CLASSES = {
  small: 'py-2 h-9',
  large: 'py-3',
};
const RECTANGLE_CLASSES = {
  small: 'rounded-9px',
  large: 'rounded-12',
};
const BASE_ACTIVE_CLASSES = 'active:focus:ring-inset active:focus:ring-2';
const OUTLINED_CLASSES = `border border-primary text-primary hover:bg-primary-lighter-06 active:bg-primary-lighter-13 active:focus:ring-primary group-hover:bg-white`;
const PRIMARY_CLASSES = `bg-primary text-white active:focus:ring-white-40`;
export const SECONDARY_CLASSES = `bg-primary-lighter-06 text-primary hover:bg-primary-lighter-13 active:bg-primary-lighter-20 active:focus:ring-primary group-hover:bg-white`;
const TERTIARY_CLASSES = `bg-white-snow text-gray-charcoal hover:bg-black-lighter-06 focus:bg-black-lighter-13 focus:text-primary active:bg-white-snow active:focus:ring-primary`;
const QUATERNARY_CLASSES = `bg-white text-primary hover:bg-black-lighter-06 focus:bg-black-lighter-13 focus:text-primary active:bg-white-snow active:focus:ring-primary`;
const QUINARY_CLASSES = `bg-white text-gray-dark-charchoal hover:bg-black-lighter-06 focus:bg-black-lighter-13 active:bg-white-snow active:focus:ring-primary`;
const DESTRUCTIVE_CLASSES = `bg-primary-lighter-06 text-red-pure hover:bg-primary-lighter-13 active:bg-primary-lighter-20 active:focus:ring-primary`;
// No `justify-start`, as it's the flex default: Tailwind v4 orders it after `justify-center`, so it would override
// the `justify-*` classes passed through `className`
const ICON_AND_TEXT_CLASSES = {
  large: `pl-12px pr-15px`,
  small: `pl-9px pr-12px`,
};
const TEXT_ONLY_CLASSES = `px-18px justify-center`;
const ICON_ONLY_CLASSES = {
  large: `w-12 h-12 px-12px justify-center shrink-0`,
  small: `w-9 h-9 p-9px justify-center shrink-0`,
};
export const DISABLED_CLASSES = 'bg-primary-lighter-06 text-primary-lighter-60 opacity-80 cursor-not-allowed';
const SVG_CLASSES_PRIMARY = `fill-white`;
const SVG_CLASSES_SECONDARY = `fill-primary`;
const SVG_CLASSES_TERTIARY = `fill-gray-charcoal group-focus:fill-primary`;
const SVG_CLASSES_QUINARY = `fill-gray-charcoal`;
const SVG_CLASSES_DESTRUCTIVE = `fill-red-pure`;
const SVG_DISABLED_CLASSES = `fill-primary-lighter-20`;

const colorClasses = {
  primary: `${PRIMARY_CLASSES}`,
  secondary: `${SECONDARY_CLASSES}`,
  tertiary: `${TERTIARY_CLASSES}`,
  quaternary: `${QUATERNARY_CLASSES}`,
  destructive: `${DESTRUCTIVE_CLASSES}`,
  quinary: `${QUINARY_CLASSES}`,
};

const svgClasses = {
  primary: `${SVG_CLASSES_PRIMARY}`,
  secondary: `${SVG_CLASSES_SECONDARY}`,
  tertiary: `${SVG_CLASSES_TERTIARY}`,
  quaternary: `${SVG_CLASSES_TERTIARY}`,
  quinary: `${SVG_CLASSES_QUINARY}`,
  destructive: `${SVG_CLASSES_DESTRUCTIVE}`,
};

export type ButtonColors = 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'quinary' | 'destructive';
export enum ButtonShapes {
  rectangle = 'rectangle',
  circle = 'circle',
  custom = 'custom',
}

export type ButtonProps = Omit<JSX.IntrinsicElements['button'], 'color'> &
  CommonProps & {
    icon?: (iconClasses: string, color?: string) => ReactNode;
    rightSideIcon?: ReactNode;
    rightSideIconWrapperCss?: string;
    bottomIcon?: ReactNode;
    bottomIconWrapperCss?: string;
    text?: string | number;
    disabled?: boolean;
    color?: ButtonColors;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    tooltip?: string | ReactNode;
    shape?: ButtonShapes;
    textClasses?: string;
    tooltipProps?: TooltipOptions;
    dataTourId?: string;
    size?: 'small' | 'large';
    variant?: 'default' | 'outlined';
    form?: string;
    onHover?: () => void;
  };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      icon,
      bottomIcon,
      bottomIconWrapperCss,
      rightSideIcon,
      rightSideIconWrapperCss,
      text,
      disabled = false,
      color = 'primary',
      onClick,
      tooltip,
      type = 'button',
      className,
      shape = ButtonShapes.rectangle,
      tooltipProps,
      textClasses,
      dataTourId,
      size = 'large',
      variant = 'default',
      onHover,
      ...props
    },
    ref,
  ) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const getButtonColorClasses = (color: ButtonColors) => colorClasses[color];
    const titleRef = useRef(null);
    const isOverflow = useCheckOverflow(titleRef);
    const isFocused = useIsFocused(buttonRef);
    const { disabled: isTooltipDisabled } = tooltipProps ?? {};

    const getIconClasses = (color: ButtonColors, disabled: boolean) => {
      if (disabled) return `${SVG_DISABLED_CLASSES}`;
      return svgClasses[color];
    };

    const iconColor = useMemo(() => {
      if (disabled) return 'var(--color-primary-lighter-20)';
      switch (color) {
        case 'primary':
          return 'white';
        case 'secondary':
          return 'var(--color-primary)';
        case 'tertiary':
          return 'var(--color-gray-charcoal)';
        case 'destructive':
          return '#ED290E';
        default:
          return undefined;
      }
    }, [color, disabled]);

    const computedClasses = useMemo(() => {
      const iconClasses = classNames(getIconClasses(color, disabled), 'shrink-0');
      const buttonClasses = classNames(
        BASE_CLASSES,
        SIZE_CLASSES[size],
        {
          [ICON_AND_TEXT_CLASSES[size]]: icon && !isNil(text),
          [TEXT_ONLY_CLASSES]: !icon && !isNil(text),
          [ICON_ONLY_CLASSES[size]]: icon && isNil(text),
          [getButtonColorClasses(color)]: !disabled && variant === 'default',
          [BASE_ACTIVE_CLASSES]: !disabled,
          [DISABLED_CLASSES]: disabled,
          [OUTLINED_CLASSES]: variant === 'outlined',
          [RECTANGLE_CLASSES[size]]: shape === ButtonShapes.rectangle,
          'rounded-full': shape === ButtonShapes.circle,
        },
        className,
      );

      return { buttonClasses, iconClasses };
    }, [color, disabled, size, icon, text, variant, shape, className]);

    return (
      <TooltipWrapper
        interactive={!isString(tooltip)}
        content={tooltip ?? text}
        disabled={isTooltipDisabled || (!tooltip && !isOverflow)}
        open={
          (!isTooltipDisabled &&
            tooltip &&
            typeof tooltip === 'string' &&
            isFocused &&
            buttonRef.current?.matches(':focus-visible')) ||
          undefined
        }
        {...tooltipProps}
      >
        <button
          data-tour={dataTourId}
          ref={useMergeRefs([ref, buttonRef])}
          className={computedClasses.buttonClasses}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            if (!onClick) return;
            onClick(e);
          }}
          type={type}
          {...props}
          // A disabled button does not trigger onMouseEnter/onMouseLeave events
          disabled={false}
          onMouseEnter={onHover}
        >
          {icon && icon(computedClasses.iconClasses, iconColor)}
          {text && (
            <p ref={titleRef} className={classNames('truncate', textClasses)}>
              {text}
            </p>
          )}
          {rightSideIcon && (
            <div className={classNames('ml-auto shrink-0', rightSideIconWrapperCss)}>{rightSideIcon}</div>
          )}
          {bottomIcon && (
            <div
              className={classNames('absolute left-1/2 -translate-x-1/2', bottomIconWrapperCss, {
                'bottom-1': size === 'large',
                'bottom-0.5': size === 'small',
              })}
            >
              {bottomIcon}
            </div>
          )}
        </button>
      </TooltipWrapper>
    );
  },
);
